import { getDynamicCurrency } from '@/util-conversion/currency';
import BrandmarkSealFoil from '@/icons/brandmark-seal-foil';
import OfferGift from '@/icons/offer-gift';
import PDPSubscriptionFeatures from './pdp-subscription-features';
import Spacer from '@/common/components/spacer';
import BrandmarkSealGotham from '@/icons/brandmark-seal-gotham';
import GradientText from '@/common/components/gradient-text';
import webpWithPngFallback from '@/common/utils/webp-with-png-fallback';
import usePDPMembershipCardSticky from '@/catalog/hooks/use-pdp-membership-card-sticky';
interface PDPMembershipCardProps {
  membershipCost?: string;
  gasSaverCents: string;
  PRISTINE_HERO_PDP?: boolean;
  PUBLIC_CDN_URL?: string;
  vehicleImagesColumnRef?: React.RefObject<HTMLDivElement | null>;
}

export default function PDPMembershipCard({
  membershipCost,
  gasSaverCents,
  PRISTINE_HERO_PDP = false,
  PUBLIC_CDN_URL = '',
  vehicleImagesColumnRef,
}: PDPMembershipCardProps) {
  const imageUrl = webpWithPngFallback(
    `${PUBLIC_CDN_URL}/web/images/pdp/membership-card-bg.webp`
  );
  const { bottomTabRef } = usePDPMembershipCardSticky({
    vehicleImagesColumnRef,
    PRISTINE_HERO_PDP,
  });

  return PRISTINE_HERO_PDP ? (
    <div className="relative m-0 w-full">
      {/* Need these spacers to act as placeholder heights of the card so that the sticky bottom tab is positioned correctly when it scrolls out into view. 
          The spacers need to be the height of the card minus 48px to account for the border radius of the card and the matching border radius at the 
          top of the bottom tab to match the top of the card when it is scrolled beneath the card. */}
      <Spacer variant="height222" hideForBreakpoints={['sm', 'md']} />
      <Spacer variant="height214" hideForBreakpoints={['lg', 'xl', '2xl']} />

      {/* Sticky bottom tab */}
      <div
        className="relative flex w-full flex-col rounded-3xl bg-flexcar-neutral100 py-[16px] pl-[16px] pr-[8px] lg:sticky lg:bottom-[var(--sticky-bottom)]"
        ref={bottomTabRef}
      >
        {/* Height 48px accounts for the 24px border radius of the card so that the white background of the tab is visible where 
            the corner rounds and the 24px of border radius needed at the top so that the white background isn't visible when the
            tab is completely scrolled beneath the card and rests at the top of the card. */}
        <Spacer variant="height48" />
        <div className="flex flex-row items-center gap-[8px]">
          <div className="flex h-[48px] w-[48px] flex-shrink-0 items-center justify-center rounded-full bg-flexcar-neutral90">
            <OfferGift className="h-[18px] w-[24px] flex-shrink-0" />
          </div>
          <div className="flex w-full flex-col">
            <p className="body2-tight font-semibold">Drive now, pay later.</p>
            <p className="body3-tight text-[14px] leading-[22px] tracking-normal text-flexcar-neutral1">
              The first 2 weeks of your membership are on us.
            </p>
          </div>
        </div>
      </div>

      {/* Membership card */}
      <div className="absolute top-0 flex w-full items-center justify-center rounded-3xl bg-[linear-gradient(148deg,_#FFF_30%,_#969FBA_100%)] shadow-membershipCardShadow">
        {/* Note: There is 24px of padding for the card, but 2px of that padding includes the gradient border. */}
        <div className="relative m-[1.5px] flex h-full w-full flex-col gap-[32px] rounded-[22.5px] p-[22px]">
          {/* Need z-1 to get content above background image */}
          <div className="z-1 flex w-full items-center gap-[12px]">
            <BrandmarkSealGotham className="h-[40px] w-[40px] flex-shrink-0 lg:h-[48px] lg:w-[48px]" />
            <div className="relative flex w-full items-center justify-between">
              {/* Foreground gradient text */}
              <GradientText
                variant="h2"
                gradient="radial-gradient(554.06% 260.25% at 125% 128.26%, #0F112E 21.29%, #53759B 27.83%, #131636 40.5%, #010118 100%)"
                className="absolute font-sans-tight text-[20px] leading-[32px] tracking-normal md:text-[22px]"
              >
                Flexcar Membership
              </GradientText>

              {/* Background gradient text (outline around foreground) */}
              <GradientText
                variant="h2"
                gradient="linear-gradient(to top, #FFFFFF, #95A0BE)"
                className="text-nowrap font-sans-tight text-[20px] leading-[32px] tracking-normal md:text-[22px]"
                asOutline="2px transparent"
              >
                Flexcar Membership
              </GradientText>

              <p className="body3-tight rounded-full border-2 border-flexcar-membershipDarkBlue bg-[linear-gradient(123deg,_#ACB1C0_-10.47%,_#E4E7EB_99.53%)] px-[12px] py-[2px] font-semibold leading-[22px] text-flexcar-neutral1">
                {getDynamicCurrency(Number(membershipCost))}/yr
              </p>
            </div>
          </div>
          <PDPSubscriptionFeatures
            gasSaverCents={gasSaverCents}
            PRISTINE_HERO_PDP={PRISTINE_HERO_PDP}
            className="z-1"
          />
          <img
            src={imageUrl}
            alt="Membership card background"
            className="absolute inset-0 z-0 h-full w-full rounded-[22.5px] object-cover"
            fetchPriority="high"
            loading="eager"
          />
        </div>
      </div>
    </div>
  ) : (
    <div className="relative mt-[8px] w-[calc(100%-32px)] overflow-hidden rounded-3xl border border-flexcar-neutral80 lg:top-0 lg:mt-[24px] lg:w-[424px]">
      <div className="bg-[linear-gradient(180.07deg,_#FFF_0.6%,_#EFF4F8_76.6%)]">
        <div className="mx-[24px] mt-[16px] flex h-[40px] gap-[16px]">
          <BrandmarkSealFoil className="flex-shrink-0" />
          <div className="flex w-full items-center justify-between">
            <h3>Flexcar Membership</h3>
            <p className="body3-strong rounded-full border border-flexcar-neutral60 px-[8px] py-[4px] leading-[16px] text-flexcar-neutral1">
              {getDynamicCurrency(Number(membershipCost))}/yr
            </p>
          </div>
        </div>
        <PDPSubscriptionFeatures gasSaverCents={gasSaverCents} />
      </div>
      <div className="flex flex-row gap-[8px] px-[24px] py-[16px]">
        <OfferGift className="relative top-[2px] flex-shrink-0" />
        <div className="flex w-full items-center">
          <p className="body3">
            <span className="body3-strong">Worry-free. </span>Membership billed after 2
            weeks.
          </p>
        </div>
      </div>
    </div>
  );
}
